import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Search } from "lucide-react";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { buscarItensNaoInventariados } from "@/services/itensNaoInventariados";
import type { ItemNaoInventariado } from "@/types/item-nao-inventariado";

const PAGE_SIZE = 12;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ModalItensNaoInventariados({ open, onOpenChange }: Props) {
  const { impressaoConfig } = useImpressaoConfig();

  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [itens, setItens] = useState<ItemNaoInventariado[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // debounce da busca -> reseta para a primeira pagina
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBuscaDebounced(busca.trim());
      setPage(0);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [busca]);

  const carregar = useCallback(async () => {
    if (!open || !impressaoConfig?.id_usuario) return;
    setLoading(true);
    setErro(false);
    try {
      const resp = await buscarItensNaoInventariados({
        id_usuario: impressaoConfig.id_usuario,
        page,
        pageSize: PAGE_SIZE,
        search: buscaDebounced || undefined,
      });
      setItens(resp.items || []);
      setTotal(resp.total || 0);
    } catch {
      setErro(true);
      setItens([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, impressaoConfig?.id_usuario, page, buscaDebounced]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // ao abrir, zera o estado de busca/pagina
  useEffect(() => {
    if (open) {
      setBusca("");
      setBuscaDebounced("");
      setPage(0);
    }
  }, [open]);

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-7xl flex-col gap-4 overflow-hidden sm:max-w-7xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Impressão bloqueada — itens não inventariados
          </DialogTitle>
          <DialogDescription>
            Existem etiquetas impressas que ainda não foram conferidas por
            inventário. Conclua o inventário desses itens antes de imprimir
            novamente.
          </DialogDescription>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por EPC ou descrição..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="min-h-[320px] flex-1 overflow-auto rounded-md border">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead className="w-[45%]">EPC</TableHead>
                <TableHead className="w-[25%]">Descrição</TableHead>
                <TableHead className="w-[30%]">Categoria</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-64 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : erro ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-64 text-center text-destructive"
                  >
                    Não foi possível verificar o inventário. Por segurança, a
                    impressão permanece bloqueada.
                  </TableCell>
                </TableRow>
              ) : itens.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-64 text-center text-muted-foreground"
                  >
                    Nenhum item encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                itens.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell className="break-all font-mono text-xs">
                      {item.epc}
                    </TableCell>
                    <TableCell className="break-words">
                      {item.descricao || "-"}
                    </TableCell>
                    <TableCell className="break-words">
                      {item.categoria || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {total} {total === 1 ? "item pendente" : "itens pendentes"}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={loading || page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <span className="text-sm">
              {page + 1} / {totalPaginas}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || page + 1 >= totalPaginas}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalItensNaoInventariados;
